import { Card, CardContent } from './card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';
import { Info } from 'lucide-react';

// Metric definitions with clear, simple descriptions
const METRIC_INFO = {
  total_reviews: {
    title: 'Analysed Reviews',
    description: 'Total count of all customer reviews analysed from every connected marketplace.',
  },
  avg_rating: {
    title: 'Average Rating',
    description: 'The average star rating (1-5) across all reviews.',
  },
  positive: {
    title: 'Positive',
    description: 'Reviews with happy, satisfied, or praising sentiment.',
  },
  neutral: {
    title: 'Neutral',
    description: 'Reviews with balanced or mixed sentiment.',
  },
  negative: {
    title: 'Negative',
    description: 'Reviews with complaints, issues, or dissatisfaction.',
  },
  total_skus: {
    title: 'Analysed SKUs',
    description: 'Number of unique products with review data.',
  },
  total_brands: {
    title: 'Total Brands',
    description: 'Number of brands being tracked.',
  },
  composite_score: {
    title: 'Score',
    description: 'Combined ranking based on rating, sentiment, and volume.',
  },
  sentiment_score: {
    title: 'Sentiment',
    description: 'Percentage of positive reviews for this item.',
  },
};

export function MetricCard({ 
  metricKey, 
  icon: Icon, 
  iconColor = 'text-muted-foreground',
  label, 
  value, 
  suffix = '', 
  subtext,
  onClick,
  className = '',
  highlight = false,
  highlightColor = '#2eb4ac',
}) {
  const info = METRIC_INFO[metricKey] || { title: label, description: 'Metric information' };
  
  return (
    <Card 
      className={`card-hover ${onClick ? 'cursor-pointer' : ''} ${highlight ? `bg-gradient-to-br from-[${highlightColor}]/10 to-transparent` : ''} ${className}`}
      onClick={onClick}
      data-testid={`kpi-${metricKey}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 text-muted-foreground">
            {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
            <span className="text-xs font-medium">{label}</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                <Info className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              side="top" 
              className="max-w-[220px] bg-popover border-border shadow-xl"
            >
              <p className="font-semibold text-sm text-foreground mb-1">{info.title}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{info.description}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className={`text-2xl font-bold ${highlight ? `text-[${highlightColor}]` : ''}`}>
          {value}{suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
        </p>
        {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
      </CardContent>
    </Card>
  );
}

export { METRIC_INFO };
